import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { PageHeader } from "@/components/PageHeader";
import { TextField, SelectField, FieldHelp, primaryBtnCls, outlineBtnCls } from "@/components/FormFields";
import {
  JURISDICTIONS, ENVIRONMENTS, WCAG_TARGETS,
  environmentLabel, wcagTargetLabel, ENVIRONMENT_STYLES,
} from "@/lib/taxonomy";

// Sentinel value for the client dropdown option that opens the inline client
// fields, rather than selecting an existing client.
const NEW_CLIENT = "__new__";

const emptyForm = {
  client_id: "",
  name: "",
  base_url: "",
  environment: "production",
  wcag_target: "wcag22aa",
  new_client_name: "",
  new_client_email: "",
  new_client_jurisdiction: "eu",
};

const EnvironmentTag = ({ value }) => (
  <span className={`inline-block border rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide ${ENVIRONMENT_STYLES[value] || ENVIRONMENT_STYLES.production}`}>
    {environmentLabel(value)}
  </span>
);

export default function Sites() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilter = searchParams.get("client") || "";

  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const creatingClient = form.client_id === NEW_CLIENT;

  const load = useCallback(() => {
    const params = clientFilter ? { client_id: clientFilter } : {};
    api.get("/sites", { params }).then((r) => setSites(r.data)).catch(() => toast.error("Could not load sites"));
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [clientFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // The workspace WCAG preference seeds the form, so the common case is one
    // fewer decision per site.
    api.get("/me").then((r) => {
      const t = r.data.pref_wcag_target || "wcag22aa";
      setForm((f) => ({ ...f, wcag_target: t }));
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (creatingClient) {
        // The client and its first site are created together by POST /clients,
        // the same transactional path the Clients page uses. No second endpoint
        // and no risk of a client created without the site that justified it.
        const { data } = await api.post("/clients", {
          name: form.new_client_name,
          contact_email: form.new_client_email || null,
          jurisdiction: form.new_client_jurisdiction || null,
          first_site: {
            name: form.name,
            base_url: form.base_url,
            wcag_target: form.wcag_target,
            environment: form.environment,
          },
        });
        track("client_created", { with_first_site: true, jurisdiction: form.new_client_jurisdiction || "unset" });
        track("site_created", { wcag_target: form.wcag_target, environment: form.environment });
        toast.success(`Client ${data.name} and site created`);
      } else {
        await api.post("/sites", {
          client_id: form.client_id,
          name: form.name,
          base_url: form.base_url,
          wcag_target: form.wcag_target,
          environment: form.environment,
        });
        track("site_created", { wcag_target: form.wcag_target, environment: form.environment });
        toast.success("Site created");
      }
      setShowForm(false);
      setForm((f) => ({ ...emptyForm, wcag_target: f.wcag_target }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create site");
    } finally {
      setBusy(false);
    }
  };

  const filteredClient = clients.find((c) => c.id === clientFilter);

  return (
    <div data-testid="sites-page">
      <PageHeader
        title="Sites"
        description="Each site accumulates audit history and a persistent issue ledger."
        actions={
          <button data-testid="new-site-btn" onClick={() => setShowForm(!showForm)} className={primaryBtnCls}>
            New site
          </button>
        }
      />

      {clientFilter && (
        <div className="flex items-center gap-3 mb-4" data-testid="sites-client-filter">
          <p className="text-sm text-ink-muted">
            Showing sites for <span className="font-medium text-ink">{filteredClient?.name || "one client"}</span>.
          </p>
          <button onClick={() => setSearchParams({})} className={outlineBtnCls} data-testid="sites-clear-filter">
            Show all sites
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={create} className="bg-white border border-gray-200 rounded-sm p-5 mb-6 max-w-lg">
          <SelectField
            id="site-client" testId="site-client-select" label="Client" required
            options={[...clients.map((c) => ({ value: c.id, label: c.name })), { value: NEW_CLIENT, label: "Create a new client" }]}
            placeholder="Select a client"
            value={form.client_id} onChange={set("client_id")}
          />

          {creatingClient && (
            <div className="border-l-2 border-[#16324F] pl-4 mb-4" data-testid="site-new-client-fields">
              <TextField
                id="site-new-client-name" testId="site-new-client-name-input" label="New client name" required
                value={form.new_client_name} onChange={set("new_client_name")}
              />
              <TextField
                id="site-new-client-email" testId="site-new-client-email-input" label="Contact email" hint="(optional)"
                type="email" value={form.new_client_email} onChange={set("new_client_email")}
              />
              <SelectField
                id="site-new-client-jurisdiction" testId="site-new-client-jurisdiction-select" label="Jurisdiction"
                options={JURISDICTIONS} placeholder="Not set"
                value={form.new_client_jurisdiction} onChange={set("new_client_jurisdiction")}
                className="mb-0"
              />
            </div>
          )}

          <TextField
            id="site-name" testId="site-name-input" label="Site name" required
            value={form.name} onChange={set("name")}
          />
          <TextField
            id="site-url" testId="site-url-input" label="Base URL" required mono
            placeholder="https://client-site.com"
            value={form.base_url} onChange={set("base_url")}
          />
          <SelectField
            id="site-environment" testId="site-environment-select" label="Environment"
            options={ENVIRONMENTS} value={form.environment} onChange={set("environment")}
            describedBy="site-environment-help" className="mb-1.5"
          />
          <FieldHelp id="site-environment-help">
            Staging and local sites sit behind logins or a VPN, so an external crawler never
            reaches them. The extension audits them in your own browser session.
          </FieldHelp>
          <SelectField
            id="site-target" testId="site-target-select" label="WCAG target"
            options={WCAG_TARGETS} value={form.wcag_target} onChange={set("wcag_target")}
            className="mb-4"
          />

          <button data-testid="site-create-submit" type="submit" disabled={busy} className={primaryBtnCls}>
            {busy ? "Creating..." : creatingClient ? "Create client and site" : "Create site"}
          </button>
        </form>
      )}

      <div className="table-card" tabIndex={0} role="region" aria-label="List of sites">
        <table className="w-full min-w-[960px] text-left border-collapse text-sm" data-testid="sites-table">
          <caption className="sr-only">List of sites</caption>
          <thead>
            <tr className="border-b border-gray-300">
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Site</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Client</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Environment</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Target</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Open issues</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Last audit</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {sites.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-ink-muted" data-testid="sites-empty">
                  No sites yet.
                </td>
              </tr>
            )}
            {sites.map((s) => (
              <tr key={s.id} className="border-b border-gray-200 last:border-0">
                <td className="px-5 py-3">
                  <Link to={`/sites/${s.id}`} data-testid={`site-link-${s.id}`}
                    className="font-medium text-[#16324F] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm">
                    {s.name}
                  </Link>
                  {/* The URL is the longest string in the row and would set the
                      column's width on its own, so it is capped and clipped. */}
                  <p className="font-mono text-xs text-ink-muted max-w-[320px] truncate" title={s.base_url}>{s.base_url}</p>
                </td>
                <td className="px-5 py-3">{s.client_name}</td>
                <td className="px-5 py-3" data-testid={`site-environment-${s.id}`}>
                  <EnvironmentTag value={s.environment} />
                </td>
                <td className="px-5 py-3 font-mono text-xs whitespace-nowrap">{wcagTargetLabel(s.wcag_target)}</td>
                <td className="px-5 py-3 font-mono text-right">{s.open_issues}</td>
                <td className="px-5 py-3 font-mono text-xs text-right text-ink-muted">
                  {s.last_audit_at ? new Date(s.last_audit_at).toLocaleDateString() : "-"}
                </td>
                {/* The site name is also a link, but an explicit action makes the
                    row's affordance obvious instead of something to discover. */}
                <td className="px-5 py-3 text-right">
                  <Link to={`/sites/${s.id}`} data-testid={`site-view-${s.id}`} className={outlineBtnCls}>
                    View<span className="sr-only"> {s.name}</span>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
